import { Field, FieldType, FieldValueObject, FieldValue, addFieldValue } from './field';
import { FieldObjectCst } from './field-object';

abstract class AbstractFieldListCst extends Field<FieldValueObject[]> {
	readonly entries: FieldObjectCst[];
	constructor(
		isVar: boolean,
		type: FieldType,
		name: string,
		entries: FieldObjectCst[]
	) {
		super(isVar, type, name);
		this.entries = entries;
	}

	value() {
		const values: FieldValueObject[] = [];
		for (const entry of this.entries) {
			const entryValue = entry.value();
			if (entryValue !== undefined) {
				values.push(entryValue);
			}
		}
		return values.length === 0 ? undefined : values;
	}

	getEntryFieldNames() {
		return this.entries.length === 0 ? [] : this.entries[0].fieldNames;
	}
}

export class FieldListCst extends AbstractFieldListCst {
	private readonly entryMaker: (entryValue: FieldValueObject) => FieldObjectCst;
	constructor(
		name: string,
		entries: FieldObjectCst[],
		entryMaker: (entryValue: FieldValueObject) => FieldObjectCst
	) {
		super(false, 'listCst', name, entries);
		this.entryMaker = entryMaker;
	}

	collectVarValue() {
		// noop
	}

	setValue(value: FieldValue) {
		if (value === undefined) {
			this.entries.splice(0, this.entries.length);
			return;
		}
		if (Array.isArray(value)) {
			const { entries } = this;
			if (entries.length > value.length) {
				entries.splice(0, entries.length - value.length);
			}
			for (const [index, entry] of entries.entries()) {
				entry.setValue(value[index]);
			}
			for (let index = entries.length; index < value.length; index++) {
				this.entries.push(this.entryMaker(value[index]));
			}
			return;
		}
		throw new Error(`unable to set value; expected array, but found ${ JSON.stringify(value) }`);
	}
}

export class FieldListVar extends AbstractFieldListCst {
	readonly path: string;
	readonly maxItems: number;
	private readonly entryMaker: () => FieldObjectCst;
	constructor(
		name: string,
		path: string,
		entries: FieldObjectCst[],
		entryMaker: () => FieldObjectCst,
		maxItems: number
	) {
		super(true, 'listVar', name, entries);
		this.path = path;
		this.entryMaker = entryMaker;
		this.maxItems = maxItems;
	}

	isNewEntryAllowed() {
		return this.entries.length < this.maxItems;
	}

	addNewEntry() {
		if (!this.isNewEntryAllowed()) {
			throw new Error('new entry is forbidden');
		}
		this.entries.push(this.entryMaker());
	}

	removeEntryAt(index: number) {
		if (index < 0 || this.entries.length <= index) {
			throw new Error(`entry index ${ index } not supported (array length: ${ this.entries.length })`);
		}
		this.entries.splice(index, 1);
	}

	collectVarValue(object: FieldValueObject) {
		addFieldValue(
			this.path,
			this.entries.map(entry => {
				const entryObject: FieldValueObject = {};
				entry.collectVarValue(entryObject);
				return entryObject;
			}),
			object
		);
	}

	setValue(value: FieldValue) {
		if (value === undefined) {
			this.entries.splice(0, this.entries.length);
			return;
		}
		if (Array.isArray(value)) {
			while (this.entries.length < value.length) {
				this.addNewEntry();
			}
			if (this.entries.length > value.length) {
				this.entries.splice(0, this.entries.length - value.length);
			}
			for (let index = 0; index < this.entries.length; index++) {
				this.entries[index].setValue(value[index]);
			}
			return;
		}
		throw new Error(`unable to set value; expected array, but found ${ JSON.stringify(value) }`);
	}
}
